import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotesStore, useUIStore } from '../store';
import api from '../api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger
} from '../components/ui/context-menu';
import { Maximize2, Minimize2, RefreshCw } from 'lucide-react';

export default function GraphView() {
  const navigate = useNavigate();
  const svgRef = useRef(null);
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    loadGraph();
  }, []);

  const loadGraph = async () => {
    setIsLoading(true);
    try {
      const data = await api.getGraph();
      setGraphData(data);
    } catch (error) {
      console.error('Failed to load graph:', error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (graphData.nodes.length === 0 || !svgRef.current) return;

    const svg = svgRef.current;
    const width = svg.clientWidth || 800;
    const height = svg.clientHeight || 600;

    const nodes = graphData.nodes.map((n, i) => ({
      ...n,
      x: width / 2 + (Math.random() - 0.5) * 300,
      y: height / 2 + (Math.random() - 0.5) * 300,
      vx: 0,
      vy: 0
    }));

    const links = graphData.links
      .map(l => ({
        source: nodes.find(n => n.id === l.source_id),
        target: nodes.find(n => n.id === l.target_id)
      }))
      .filter(l => l.source && l.target);

    // Force simulation
    const repulsion = 8000;
    const attraction = 0.005;
    const centerGravity = 0.01;
    const damping = 0.85;

    for (let iter = 0; iter < 150; iter++) {
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[j].x - nodes[i].x;
          const dy = nodes[j].y - nodes[i].y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const force = repulsion / (dist * dist);
          nodes[i].vx -= (dx / dist) * force;
          nodes[i].vy -= (dy / dist) * force;
          nodes[j].vx += (dx / dist) * force;
          nodes[j].vy += (dy / dist) * force;
        }
      }

      for (const link of links) {
        const dx = link.target.x - link.source.x;
        const dy = link.target.y - link.source.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const force = dist * attraction;
        link.source.vx += dx * force;
        link.source.vy += dy * force;
        link.target.vx -= dx * force;
        link.target.vy -= dy * force;
      }

      for (const node of nodes) {
        node.vx += (width / 2 - node.x) * centerGravity;
        node.vy += (height / 2 - node.y) * centerGravity;
        node.vx *= damping;
        node.vy *= damping;
        node.x += node.vx;
        node.y += node.vy;
        node.x = Math.max(60, Math.min(width - 60, node.x));
        node.y = Math.max(60, Math.min(height - 60, node.y));
      }
    }

    // Render
    svg.innerHTML = '';

    // Defs for gradients
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    const gradient = document.createElementNS('http://www.w3.org/2000/svg', 'radialGradient');
    gradient.id = 'nodeGradient';
    gradient.innerHTML = '<stop offset="0%" stop-color="hsl(var(--primary))" /><stop offset="100%" stop-color="hsl(var(--primary) / 0.6)" />';
    defs.appendChild(gradient);
    svg.appendChild(defs);

    // Links
    for (const link of links) {
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', link.source.x);
      line.setAttribute('y1', link.source.y);
      line.setAttribute('x2', link.target.x);
      line.setAttribute('y2', link.target.y);
      line.setAttribute('stroke', 'hsl(var(--border))');
      line.setAttribute('stroke-width', '1');
      svg.appendChild(line);
    }

    // Nodes
    for (const node of nodes) {
      const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.style.cursor = 'pointer';
      g.onclick = () => navigate(`/note/${node.id}`);

      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', node.x);
      circle.setAttribute('cy', node.y);
      circle.setAttribute('r', '10');
      circle.setAttribute('fill', 'hsl(var(--primary))');
      circle.setAttribute('stroke', 'hsl(var(--primary))');
      circle.setAttribute('stroke-width', '2');
      circle.setAttribute('stroke-opacity', '0.5');
      g.appendChild(circle);

      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', node.x);
      text.setAttribute('y', node.y + 24);
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('fill', 'hsl(var(--foreground))');
      text.setAttribute('font-size', '12');
      text.setAttribute('font-weight', '500');
      text.textContent = node.title.length > 25 ? node.title.slice(0, 25) + '...' : node.title;
      g.appendChild(text);

      g.onmouseenter = () => {
        circle.setAttribute('r', '12');
        circle.setAttribute('stroke-width', '3');
      };
      g.onmouseleave = () => {
        circle.setAttribute('r', '10');
        circle.setAttribute('stroke-width', '2');
      };

      svg.appendChild(g);
    }
  }, [graphData]);

  return (
    <div className={`h-full flex flex-col ${isFullscreen ? 'fixed inset-0 z-50 bg-background' : ''}`}>
      <div className="flex items-center justify-between px-4 py-2 border-b bg-card">
        <h2 className="text-lg font-semibold">Knowledge Graph</h2>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={loadGraph}>
            <RefreshCw size={16} />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setIsFullscreen(!isFullscreen)}>
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </Button>
        </div>
      </div>

      <div className="flex-1 relative bg-card border rounded-b-lg m-2 mt-0 overflow-hidden">
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : graphData.nodes.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <p className="text-lg mb-2">No notes to display</p>
              <p className="text-sm">Create some notes with [[links]] to see the graph</p>
            </div>
          </div>
        ) : null}

        <svg ref={svgRef} className="w-full h-full" />

        {/* Legend */}
        <div className="absolute bottom-4 left-4 text-xs text-muted-foreground bg-background/80 backdrop-blur px-3 py-2 rounded-lg border">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded-full bg-primary"></div>
            <span>Note</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-px bg-border"></div>
            <span>Link</span>
          </div>
        </div>
      </div>
    </div>
  );
}
